import { useCallback } from 'react'
import { MobileScreen } from '@mobileTypes'
import {
  useCopyMobileScreenMutation,
  useCreateMobileScreenMutation,
  useDeleteMobileScreenMutation,
  useFetchAllMobileScreensQuery,
  useUpdateMobileScreenMutation,
} from '../services'

export const useMobileScreens = () => {
  const { data: screens = [], isLoading: loading } = useFetchAllMobileScreensQuery()
  const [createMobileScreen] = useCreateMobileScreenMutation()
  const [updateMobileScreen] = useUpdateMobileScreenMutation()
  const [deleteMobileScreen] = useDeleteMobileScreenMutation()
  const [copyMobileScreen] = useCopyMobileScreenMutation()

  const createScreen = useCallback(
    (screenData: Omit<MobileScreen, 'id' | 'createdAt' | 'updatedAt'>): Promise<MobileScreen> =>
      createMobileScreen(screenData).unwrap(),
    [createMobileScreen]
  )

  const updateScreen = useCallback(
    (id: string, screenData: Partial<MobileScreen>): Promise<MobileScreen> => {
      const numericId = Number(id)
      const existing = screens.find(screen => screen.id === numericId)
      const merged: MobileScreen = {
        ...(existing ?? ({} as MobileScreen)),
        ...screenData,
        id: numericId,
      }

      return updateMobileScreen(merged).unwrap()
    },
    [screens, updateMobileScreen]
  )

  const deleteScreen = useCallback(
    async (id: string | number): Promise<void> => {
      await deleteMobileScreen(String(id)).unwrap()
    },
    [deleteMobileScreen]
  )

  const getScreenById = useCallback(
    (id: string): MobileScreen | undefined => screens.find(screen => screen.id === Number(id)),
    [screens]
  )

  const copyScreen = useCallback(
    async (id: string | number, newName: string, newInternalId: string): Promise<MobileScreen | null> => {
      try {
        return await copyMobileScreen({ id: String(id), name: newName, internalId: newInternalId }).unwrap()
      } catch {
        return null
      }
    },
    [copyMobileScreen]
  )

  return {
    screens,
    loading,
    createScreen,
    updateScreen,
    deleteScreen,
    getScreenById,
    copyScreen,
  }
}
